/**
 * Planning where a PDF citation's highlight goes.
 *
 * These are the steps of the PDF viewer's highlight-planning effect. A measured
 * anchor beats any search, so `planFromAnchor` runs first and, when it yields a
 * plan, nothing is searched for. Otherwise `matchCitationText` searches the whole
 * extracted text and `planFromTextMatch` turns a match into boxes: it finds the
 * start page from the citation's head, the end page from its tail, maps both onto
 * text items through a normalised copy of each page, and lays boxes over the items
 * in between.
 *
 * Every rectangle a plan holds is in REFERENCE_SCALE units, the units `textLayer`
 * is baked in and the overlay is sized in. Text-matched boxes inherit those units
 * from the text layer. Stored anchor boxes arrive in PDF points at scale 1, so
 * `planFromAnchor` lifts them by the reference scale it is handed, once. That is
 * the extraction side of the pair described in `PDFViewer`, and multiplying by
 * anything else would be right at one zoom level only.
 */

import type { PdfCitationBox } from "@/types/citations";

import { boundedExtendItems } from "@/utils/citations/boundedExtend";
import { findTextMatch, normalizeText, type MatchResult } from "@/utils/textMatching";
import { logger } from "@/telemetry";

import {
  WHITESPACE_RUN,
  itemToRect,
  type CitationTextItem,
  type PageHighlightPlan,
} from "./pdfCitationTypes";

/** How much of the citation's start and end is searched for when locating its pages. */
const SEGMENT_LENGTH = 100;

/** How much of the matched text the fallback looks for verbatim on each page. */
const MATCHED_TEXT_PROBE_LENGTH = 80;

/** How many characters of the citation and its match are logged. */
const LOG_PREVIEW_LENGTH = 100;
const SEGMENT_PREVIEW_LENGTH = 80;

/** A whole citation found on its start page at least this surely does not span pages. */
const FITS_ON_START_PAGE_CONFIDENCE = 0.8;

/**
 * How much of the citation's normalised length the start page must still hold after a
 * partial match for the citation to count as fitting there. Below 1 because extracted
 * PDF text and the indexed chunk normalise to slightly different lengths, so a citation
 * that ends at the foot of its page would otherwise read as running past it.
 */
const FITS_REMAINING_LENGTH_RATIO = 0.9;

/** The fallback start-page search accepts a page matching at least this surely. */
const FALLBACK_START_PAGE_CONFIDENCE = 0.7;

const PERCENT = 100;

/**
 * A letter or a digit in any script. Hoisted for the same reason as
 * `WHITESPACE_RUN`, and safe to share because it carries no `g` or `y` flag, so
 * `test` never reads or writes `lastIndex`.
 */
const WORD_CHAR = /[\p{L}\p{N}]/u;

/** A highlight plan and the page the view opens at. */
export interface CitationPlan {
  plan: PageHighlightPlan;
  matchedPage: number;
}

/** A plan made from a text match, with the span it covers for logging. */
export interface TextMatchPlan extends CitationPlan {
  fromPage: number;
  toPage: number;
  totalBoxes: number;
}

/** A page's text normalised the way the search sees it, with each character's item. */
interface NormalisedPage {
  text: string;
  starts: number[];
  lens: number[];
  /** The item each character came from, or -1 for a space inserted between items. */
  charToItemIndex: number[];
}

interface CitationSegments {
  headSeg: string;
  tailSeg: string;
}

interface EndPageSearch extends CitationSegments {
  citationText: string;
}

interface PageSearch {
  page: number | null;
  confidence: number;
}

interface EndIndexSearch {
  citationText: string;
  tailSeg: string;
  /** Whether the citation starts and ends on this page. */
  samePage: boolean;
  page: number;
}

interface PlanSpan {
  textLayer: CitationTextItem[];
  citationText: string;
  startPage: number;
  endPage: number;
  startIndex: number;
  endIndex: number;
}

interface ItemRange {
  fromIdx: number;
  toIdx: number;
}

const asPercent = (confidence: number) => Math.round(confidence * PERCENT) + "%";

const itemsOnPage = (textLayer: CitationTextItem[], page: number) =>
  textLayer.filter((i) => i.page === page);

const textOfPage = (textLayer: CitationTextItem[], page: number) =>
  itemsOnPage(textLayer, page)
    .map((i) => i.text)
    .join(" ");

const sortedPageNumbers = (textLayer: CitationTextItem[]) =>
  Array.from(new Set(textLayer.map((item) => item.page))).sort((a, b) => a - b);

const isWordChar = (ch: string) => WORD_CHAR.test(ch);

/**
 * Plans the highlight from stored coordinates, or returns null when there are none
 * on a page the document has, in which case the caller falls through to the search.
 *
 * Waits on `numPages`, which is known as soon as the document loads and long before
 * any text is extracted, so that a box pointing past the end of the document can be
 * dropped. The parser cannot know the page count, and a stale anchor after a
 * re-index would otherwise set `matchedPage` to a page that never renders, leaving
 * the citation unhighlighted with the text search switched off.
 */
export const planFromAnchor = (
  citationBoxes: PdfCitationBox[] | undefined,
  numPages: number,
  referenceScale: number,
): CitationPlan | null => {
  if (!(citationBoxes?.length && numPages > 0)) return null;

  const boxesOnPages = citationBoxes.filter((box) => box.page <= numPages);

  if (boxesOnPages.length < citationBoxes.length) {
    logger.warn("📐 Citation anchor points past the end of the document", {
      numPages,
      stored: citationBoxes.length,
      kept: boxesOnPages.length,
    });
  }

  if (boxesOnPages.length === 0) return null;

  const plan: PageHighlightPlan = {};
  let firstPage = Number.POSITIVE_INFINITY;

  for (const box of boxesOnPages) {
    plan[box.page] = plan[box.page] || [];
    plan[box.page].push({
      left: box.left * referenceScale,
      top: box.top * referenceScale,
      width: box.width * referenceScale,
      height: box.height * referenceScale,
    });
    firstPage = Math.min(firstPage, box.page);
  }

  logger.info("📐 Using measured citation anchor", {
    pages: Object.keys(plan).join(", "),
    boxes: boxesOnPages.length,
  });

  return { plan, matchedPage: firstPage };
};

/** Searches the whole extracted text for the citation, logging what was found. */
export const matchCitationText = (
  textLayer: CitationTextItem[],
  citationText: string,
): MatchResult | null => {
  const documentText = textLayer.map((item) => item.text).join(" ");

  logger.info("🔍 Looking for citation", {
    citationPreview: citationText.substring(0, LOG_PREVIEW_LENGTH),
    normalizedPreview: normalizeText(citationText).substring(0, LOG_PREVIEW_LENGTH),
  });

  const matchResult = findTextMatch(documentText, citationText);

  logger.info("🔍 Citation match result", { found: String(!!matchResult) });
  if (matchResult) {
    logger.info("🔍 Found text", {
      matchedTextPreview: matchResult.matchedText.substring(0, LOG_PREVIEW_LENGTH),
      method: matchResult.method,
      confidence: asPercent(matchResult.confidence),
    });
  }

  return matchResult;
};

/** The citation's head and tail, which locate its start and end pages. */
const citationSegments = (citationText: string): CitationSegments => {
  const headLen = Math.min(SEGMENT_LENGTH, citationText.length);
  const headSeg = citationText.substring(0, headLen);
  const tailLen = Math.min(SEGMENT_LENGTH, citationText.length);
  const tailSeg = citationText.substring(Math.max(0, citationText.length - tailLen));

  logger.info("🔍 Head/tail segments", {
    headPreview: headSeg.substring(0, SEGMENT_PREVIEW_LENGTH),
    tailPreview: tailSeg.substring(0, SEGMENT_PREVIEW_LENGTH),
  });

  return { headSeg, tailSeg };
};

/** The page from `fromPage` onwards whose text matches `segment` most surely. */
const bestPageFor = (
  pageNumbers: number[],
  textLayer: CitationTextItem[],
  segment: string,
  fromPage: number,
  initial: PageSearch,
): PageSearch => {
  let best = initial;

  for (const p of pageNumbers) {
    if (p < fromPage) continue;
    const r = findTextMatch(textOfPage(textLayer, p), segment);

    if (r && r.confidence > best.confidence) {
      best = { page: p, confidence: r.confidence };
    }
  }

  return best;
};

/** Finds the best page for the citation's head, logging the outcome. */
const findStartPage = (
  pageNumbers: number[],
  textLayer: CitationTextItem[],
  headSeg: string,
): number | null => {
  const start = bestPageFor(pageNumbers, textLayer, headSeg, Number.NEGATIVE_INFINITY, {
    page: null,
    confidence: 0,
  });

  if (start.page) {
    logger.info("📍 Start page found", {
      startPage: start.page,
      confidence: asPercent(start.confidence),
    });
  } else {
    logger.info("⚠️ Could not find start page via head segment; falling back to earlier method");
  }

  return start.page;
};

/**
 * If the full citation fits on the start page, the citation does not span pages, so
 * the end page is the start page and the tail-page search is skipped. This avoids the
 * cross-page fan-out observed on NG_ET_BP_048, where short common phrases at the end
 * of a chunk ("See Section 3", "Fire Safety", "competent person", etc.) appear as
 * cross-references on multiple pages, causing the tail to pick a later page and the
 * highlight to span every page in between.
 *
 * Only an exact match is the whole citation. `findTextMatch` otherwise falls back to
 * the first 100 or 50 characters, or to token overlap, which shows where the citation
 * starts and nothing about where it ends, so such a match counts as fitting only when
 * the page has room after it for the rest of the citation. Without that check every
 * text-matched citation whose head was found stayed on its first page, however much of
 * it lay on later pages. Room is measured rather than demanding an exact match because
 * extracted PDF text often differs slightly from the indexed chunk, and treating each
 * such citation as spanning pages would bring the fan-out back.
 */
const fitOnStartPage = (
  textLayer: CitationTextItem[],
  startPage: number,
  citationText: string,
): PageSearch | null => {
  const pageText = textOfPage(textLayer, startPage);
  const fullOnStartPage = findTextMatch(pageText, citationText);

  if (!(fullOnStartPage && fullOnStartPage.confidence >= FITS_ON_START_PAGE_CONFIDENCE)) {
    return null;
  }
  if (fullOnStartPage.method !== "exact") {
    const roomAfterMatch = normalizeText(pageText).length - fullOnStartPage.position;
    const citationLength = normalizeText(citationText).length;

    if (roomAfterMatch < citationLength * FITS_REMAINING_LENGTH_RATIO) {
      logger.info("📍 Citation runs past startPage; searching for its tail", {
        startPage,
        roomAfterMatch,
        citationLength,
      });

      return null;
    }
  }
  logger.info("📍 Citation fits on startPage; skipping tail-page search", {
    startPage,
    confidence: asPercent(fullOnStartPage.confidence),
  });

  return { page: startPage, confidence: fullOnStartPage.confidence };
};

/**
 * Finds the end page, logging the outcome.
 *
 * The tail page must be at or after the start page, since a citation cannot
 * legitimately have its tail before its head in reading order. When the start page
 * is unknown the constraint collapses to a search of every page.
 */
const findEndPage = (
  pageNumbers: number[],
  textLayer: CitationTextItem[],
  startPage: number | null,
  segments: EndPageSearch,
): number | null => {
  const fits = startPage ? fitOnStartPage(textLayer, startPage, segments.citationText) : null;
  const end =
    fits ??
    bestPageFor(pageNumbers, textLayer, segments.tailSeg, startPage ?? Number.NEGATIVE_INFINITY, {
      page: null,
      confidence: 0,
    });

  if (end.page) {
    logger.info("📍 End page found", { endPage: end.page, confidence: asPercent(end.confidence) });
  } else {
    logger.info("⚠️ Could not find end page via tail segment (fallback will handle)");
  }

  return end.page;
};

/**
 * The earlier page-detection approach, used when the head matched no page: the first
 * page holding the start of the matched text verbatim, or failing that the first
 * whose search for the whole citation is reasonably sure.
 */
const findFallbackStartPage = (
  pageNumbers: number[],
  textLayer: CitationTextItem[],
  matchResult: MatchResult,
  citationText: string,
): number | null => {
  const matchedTextStart = matchResult.matchedText.substring(0, MATCHED_TEXT_PROBE_LENGTH);
  let fallbackPage =
    pageNumbers.find((p) => textOfPage(textLayer, p).includes(matchedTextStart)) ?? null;

  if (!fallbackPage) {
    // try normalized search
    fallbackPage =
      pageNumbers.find((p) => {
        const r = findTextMatch(textOfPage(textLayer, p), citationText);

        return r && r.confidence >= FALLBACK_START_PAGE_CONFIDENCE;
      }) ?? null;
  }
  if (fallbackPage) logger.info("📍 Fallback start page", { startPage: fallbackPage });

  return fallbackPage;
};

/**
 * Builds a page's normalised text with each item's start and length, and the item
 * each character belongs to.
 *
 * A space is inserted between items only when the previous item ends with a word
 * character, the current starts with one, and the previous does not end with a
 * hyphen (the common hyphenation case).
 */
const buildNormPage = (items: CitationTextItem[]): NormalisedPage => {
  const starts: number[] = [];
  const lens: number[] = [];
  const charToItemIndex: number[] = [];
  let text = "";

  for (let idx = 0; idx < items.length; idx++) {
    const it = items[idx];
    const curr = normalizeText(it.text);
    const prev = idx > 0 ? normalizeText(items[idx - 1].text) : "";
    const prevLast = prev.slice(-1);
    const currFirst = curr.slice(0, 1);
    const shouldInsertSpace =
      idx > 0 && isWordChar(prevLast) && isWordChar(currFirst) && prevLast !== "-";

    if (shouldInsertSpace) {
      text += " ";
      charToItemIndex.push(-1); // separator, belongs to no item
    }

    const start = text.length;

    starts.push(start);

    // Append current normalized text and map each character to this item index
    for (let k = 0; k < curr.length; k++) {
      text += curr[k];
      charToItemIndex.push(idx);
    }

    lens.push(curr.length);
  }

  return { text, starts, lens, charToItemIndex };
};

/** The first item at or after `from`, or -1. */
const nearestItemForward = (charToItemIndex: number[], from: number): number => {
  for (let p = from; p < charToItemIndex.length; p++) {
    const idx = charToItemIndex[p];

    if (idx >= 0) return idx;
  }

  return -1;
};

/** The last item at or before `from`, or -1. */
const nearestItemBackward = (charToItemIndex: number[], from: number): number => {
  for (let p = from; p >= 0; p--) {
    const idx = charToItemIndex[p];

    if (idx >= 0) return idx;
  }

  return -1;
};

const clampToText = (position: number, text: string) =>
  Math.max(0, Math.min(position, text.length - 1));

/**
 * Maps the head match onto the item holding it. Prefers the exact character's item;
 * a separator resolves to the nearest item forward. Returns 0 when nothing maps.
 */
const mapStartPosition = (norm: NormalisedPage, position: number, page: number): number => {
  // Clamp to valid range
  const pos = clampToText(position, norm.text);
  let idx = norm.charToItemIndex[pos];

  if (idx < 0) {
    const f = nearestItemForward(norm.charToItemIndex, pos);

    idx = f >= 0 ? f : nearestItemBackward(norm.charToItemIndex, pos);
  }
  if (idx >= 0) {
    logger.info("🎯 Start position mapped via char map", { position: pos, itemIndex: idx, page });

    return idx;
  }

  return 0;
};

/** Fallback to the first-token heuristic, returning 0 when no item holds the token. */
const findFirstTokenItem = (items: CitationTextItem[], headSeg: string, page: number): number => {
  const headTokens = normalizeText(headSeg).split(WHITESPACE_RUN).filter(Boolean);
  const firstToken = headTokens[0] || "";

  for (let i = 0; i < items.length; i++) {
    if (items[i].text.toLowerCase().includes(firstToken)) {
      logger.info("🎯 Start token found via fallback", { firstToken, itemIndex: i, page });

      return i;
    }
  }

  return 0;
};

/** Determines the start item using the normalised head match position. */
const resolveStartIndex = (items: CitationTextItem[], headSeg: string, page: number): number => {
  const norm = buildNormPage(items);
  const headMatchOnStart = findTextMatch(norm.text, headSeg);

  return headMatchOnStart
    ? mapStartPosition(norm, headMatchOnStart.position, page)
    : findFirstTokenItem(items, headSeg, page);
};

/**
 * Maps the end of the tail match onto the item holding its last character. A
 * separator resolves to the nearest item backward, to avoid overshooting. Returns
 * `fallback` when nothing maps.
 */
const mapEndPosition = (
  norm: NormalisedPage,
  endPosition: number,
  page: number,
  fallback: number,
): number => {
  const endPos = clampToText(endPosition, norm.text);
  let idx = norm.charToItemIndex[endPos];

  if (idx < 0) {
    const b = nearestItemBackward(norm.charToItemIndex, endPos);

    idx = b >= 0 ? b : nearestItemForward(norm.charToItemIndex, endPos);
  }

  if (idx >= 0) {
    logger.info("🎯 End position mapped via char map", { position: endPos, itemIndex: idx, page });

    return idx;
  }

  return fallback;
};

/** Fallback to the last-token heuristic, returning the last item when none holds the token. */
const findLastTokenItem = (items: CitationTextItem[], tailSeg: string, page: number): number => {
  const tailTokens = normalizeText(tailSeg).split(WHITESPACE_RUN).filter(Boolean);
  const lastToken = tailTokens[tailTokens.length - 1] || "";

  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].text.toLowerCase().includes(lastToken)) {
      logger.info("🎯 End token found via fallback", { lastToken, itemIndex: i, page });

      return i;
    }
  }

  return items.length - 1;
};

/**
 * Determines the end item.
 *
 * When the citation lives on one page, the end is anchored to the end of the FULL
 * citation match rather than a tail-segment match. The tail is short and prone to
 * matching repeated phrases later on the page (e.g. citation tail "See Section 3"
 * also appearing in "section 3.2" further down), which causes intra-page
 * over-extension. The full citation has enough context to land at the actual end of
 * the cited content.
 */
const resolveEndIndex = (items: CitationTextItem[], search: EndIndexSearch): number => {
  const norm = buildNormPage(items);
  const tailMatchOnEnd = search.samePage
    ? (findTextMatch(norm.text, search.citationText) ?? findTextMatch(norm.text, search.tailSeg))
    : findTextMatch(norm.text, search.tailSeg);

  return tailMatchOnEnd
    ? mapEndPosition(
        norm,
        tailMatchOnEnd.position + tailMatchOnEnd.length - 1,
        search.page,
        items.length - 1,
      )
    : findLastTokenItem(items, search.tailSeg, search.page);
};

const addItemBox = (plan: PageHighlightPlan, page: number, it: CitationTextItem) => {
  plan[page] = plan[page] || [];
  plan[page].push(itemToRect(it));
};

/** The items to highlight on `page` of a citation that spans pages. */
const itemRangeOnPage = (page: number, itemCount: number, span: PlanSpan): ItemRange => {
  let fromIdx = 0;
  let toIdx = itemCount - 1;

  if (page === span.startPage) {
    fromIdx = Math.max(0, Math.min(span.startIndex, itemCount - 1));
  } else if (page === span.endPage) {
    toIdx = Math.max(0, Math.min(span.endIndex, itemCount - 1));
  }

  return { fromIdx, toIdx };
};

/**
 * Same-page case: the strict-bounded walk determines which items genuinely belong
 * to the chunk. The walk stops as soon as it sees an item that is not in the chunk
 * text, so unrelated content on the same page (other bullets, page footer, other
 * sections) is never highlighted. See src/utils/citations/boundedExtend.ts.
 */
const boxSamePage = (plan: PageHighlightPlan, span: PlanSpan): number => {
  const items = itemsOnPage(span.textLayer, span.startPage);
  const chunkNorm = normalizeText(span.citationText);
  const highlightedIndices = boundedExtendItems(items, span.startIndex, chunkNorm);

  for (const i of highlightedIndices) {
    addItemBox(plan, span.startPage, items[i]);
  }

  return highlightedIndices.length;
};

/**
 * Cross-page case: the contiguous-range logic. `boundedExtendItems` is same-page only
 * by design, so a chunk that genuinely spans pages falls back to the index-range
 * approach.
 */
const boxAcrossPages = (
  plan: PageHighlightPlan,
  span: PlanSpan,
  fromPage: number,
  toPage: number,
): number => {
  let totalBoxes = 0;

  for (let p = fromPage; p <= toPage; p++) {
    const items = itemsOnPage(span.textLayer, p);
    const { fromIdx, toIdx } = itemRangeOnPage(p, items.length, span);

    for (let i = fromIdx; i <= toIdx; i++) {
      addItemBox(plan, p, items[i]);
      totalBoxes++;
    }
  }

  return totalBoxes;
};

/** Builds the cross-page highlight plan for a span of resolved pages and items. */
const buildTextPlan = (span: PlanSpan): TextMatchPlan => {
  const plan: PageHighlightPlan = {};
  const fromPage = Math.min(span.startPage, span.endPage);
  const toPage = Math.max(span.startPage, span.endPage);
  const totalBoxes =
    span.startPage === span.endPage
      ? boxSamePage(plan, span)
      : boxAcrossPages(plan, span, fromPage, toPage);

  return { plan, matchedPage: span.startPage, fromPage, toPage, totalBoxes };
};

/**
 * Turns a match against the whole document into a cross-page plan, or returns null
 * (having warned) when no start page can be determined.
 */
export const planFromTextMatch = (
  textLayer: CitationTextItem[],
  citationText: string,
  matchResult: MatchResult,
): TextMatchPlan | null => {
  logger.info("🔍 Starting start/end page detection using head/tail segments");

  const pageNumbers = sortedPageNumbers(textLayer);
  const segments = citationSegments(citationText);
  const headStartPage = findStartPage(pageNumbers, textLayer, segments.headSeg);
  const endPage = findEndPage(pageNumbers, textLayer, headStartPage, {
    ...segments,
    citationText,
  });
  const startPage =
    headStartPage ?? findFallbackStartPage(pageNumbers, textLayer, matchResult, citationText);

  if (!startPage) {
    logger.warn("⚠️ Could not determine start page; skipping highlight plan generation");

    return null;
  }

  const startIndex = resolveStartIndex(
    itemsOnPage(textLayer, startPage),
    segments.headSeg,
    startPage,
  );
  const resolvedEndPage = endPage || startPage;
  const endIndex = resolveEndIndex(itemsOnPage(textLayer, resolvedEndPage), {
    citationText,
    tailSeg: segments.tailSeg,
    samePage: resolvedEndPage === startPage,
    page: resolvedEndPage,
  });

  return buildTextPlan({
    textLayer,
    citationText,
    startPage,
    endPage: resolvedEndPage,
    startIndex,
    endIndex,
  });
};
